import { isNil } from 'lodash';
import { BrowserTypes, DeviceTypes, OsTypes } from './constants/constants';
import { getNavigatorInstance, isIOS13Check, setDefaults } from './utils/utils';
import type UAParser from 'ua-parser-js';

type DeviceCheckFn = (device: UAParser.IDevice) => boolean;
type OSCheckFn = (device: UAParser.IOS) => boolean;
type BrowserCheckFn = (device: UAParser.IBrowser) => boolean;

// device types
export const isMobileType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Mobile;
export const isTabletType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Tablet;
export const isMobileAndTabletType: DeviceCheckFn = ({ type }) =>
  type === DeviceTypes.Mobile || type === DeviceTypes.Tablet;
export const isSmartTVType: DeviceCheckFn = ({ type }) => type === DeviceTypes.SmartTv;
export const isBrowserType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Browser;
export const isWearableType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Wearable;
export const isConsoleType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Console;
export const isEmbeddedType: DeviceCheckFn = ({ type }) => type === DeviceTypes.Embedded;
export const getMobileVendor = ({ vendor }: UAParser.IDevice): string =>
  setDefaults(vendor);
export const getMobileModel = ({ model }: UAParser.IDevice): string => setDefaults(model);
export const getDeviceType = ({ type }: UAParser.IDevice): string =>
  setDefaults(type, 'browser');

// os types
export const isAndroidType: OSCheckFn = ({ name }) => name === OsTypes.Android;
export const isWindowsType: OSCheckFn = ({ name }) => name === OsTypes.Windows;
export const isMacOsType: OSCheckFn = ({ name }) => name === OsTypes.MAC_OS;
export const isWinPhoneType: OSCheckFn = ({ name }) => name === OsTypes.WindowsPhone;
export const isIOSType: OSCheckFn = ({ name }) => name === OsTypes.IOS;
export const getOsVersion = ({ version }: UAParser.IOS): string => setDefaults(version);
export const getOsName = ({ name }: UAParser.IOS): string => setDefaults(name);

// browser types
export const isChromeType: BrowserCheckFn = ({ name }) => name === BrowserTypes.Chrome;
export const isFirefoxType: BrowserCheckFn = ({ name }) => name === BrowserTypes.Firefox;
export const isChromiumType: BrowserCheckFn = ({ name }) =>
  name === BrowserTypes.Chromium;
export const isEdgeType: BrowserCheckFn = ({ name }) => name === BrowserTypes.Edge;
export const isYandexType: BrowserCheckFn = ({ name }) => name === BrowserTypes.Yandex;
export const isSafariType: BrowserCheckFn = ({ name }) =>
  name === BrowserTypes.Safari || name === BrowserTypes.MobileSafari;
export const isMobileSafariType: BrowserCheckFn = ({ name }) =>
  name === BrowserTypes.MobileSafari;
export const isOperaType: BrowserCheckFn = ({ name }) => name === BrowserTypes.Opera;
export const isIEType: BrowserCheckFn = ({ name }) =>
  name === BrowserTypes.InternetExplorer || name === BrowserTypes.Ie;
export const isMIUIType: BrowserCheckFn = ({ name }) => name === BrowserTypes.MIUI;
export const isSamsungBrowserType: BrowserCheckFn = ({ name }) =>
  name === BrowserTypes.SamsungBrowser;
export const getBrowserFullVersion = ({ version }: UAParser.IBrowser): string =>
  setDefaults(version);
export const getBrowserVersion = ({ major }: UAParser.IBrowser): string =>
  setDefaults(major);
export const getBrowserName = ({ name }: UAParser.IBrowser): string => setDefaults(name);

// engine types
export const getEngineName = ({ name }: UAParser.IEngine): string => setDefaults(name);
export const getEngineVersion = ({ version }: UAParser.IEngine): string =>
  setDefaults(version);

export const isElectronType = (): boolean => {
  const nav = getNavigatorInstance();
  const ua = nav?.userAgent?.toLowerCase();

  return typeof ua === 'string' ? ua.includes('electron') : false;
};
export const isEdgeChromiumType = (ua: string): boolean =>
  typeof ua === 'string' && ua.includes('Edg/');

export const getIOS13 = (): boolean => {
  const nav = getNavigatorInstance();
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !isNil(nav) &&
    (/iPad|iPhone|iPod/.test(nav.platform) ||
      (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isNil((window as any).MSStream)
  );
};
export const getIPad13 = (): boolean => isIOS13Check('iPad');
export const getIphone13 = (): boolean => isIOS13Check('iPhone');
export const getIPod13 = (): boolean => isIOS13Check('iPod');
export const getUseragent = (userAg: string): string => setDefaults(userAg);
